'use client';

import { useEffect } from 'react';

import { captureFrontendError, trackEvent } from '@/lib/analytics';

// Filet ultime : ce composant remplace le layout racine quand celui-ci plante.
// À ce stade, ni globals.css ni les providers (i18n…) ne sont chargés : tout est
// autonome ici — styles inline avec les valeurs de la palette recopiées depuis
// app/globals.css, textes français en dur. Même visuel que app/error.tsx.
const PALETTE = {
  pageBackground: '#f7f6f3',
  card: '#ffffff',
  cardBorder: 'rgba(63, 146, 138, 0.2)',
  circleBackground: '#d8efeb',
  brand: '#3f928a',
  title: '#292524',
  body: '#78716c',
};

export default function GlobalError({ error }: { error: Error & { digest?: string } }) {
  useEffect(() => {
    console.error('[global-error-boundary]', error);
    captureFrontendError('react-root-boundary', error);
    trackEvent('app_error_screen_shown', { boundary: 'global' });
  }, [error]);

  return (
    <html lang="fr">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 20px',
          background: PALETTE.pageBackground,
          fontFamily: "'Poppins', system-ui, -apple-system, sans-serif",
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 448,
            background: PALETTE.card,
            border: `1px solid ${PALETTE.cardBorder}`,
            borderRadius: 24,
            padding: '32px 24px',
            textAlign: 'center',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              margin: '0 auto',
              borderRadius: '50%',
              background: PALETTE.circleBackground,
              color: PALETTE.brand,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path d="M10.3 4.2 3.4 16.1A2 2 0 0 0 5.1 19.1h13.8a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0Z" strokeLinejoin="round" />
              <path d="M12 9v4" strokeLinecap="round" />
              <path d="M12 16.2h.01" strokeLinecap="round" />
            </svg>
          </div>
          <h2 style={{ margin: '20px 0 0', fontSize: 19, fontWeight: 700, color: PALETTE.title }}>Oups, l’app a eu un pépin</h2>
          <p style={{ margin: '8px 0 0', fontSize: 14, lineHeight: 1.6, color: PALETTE.body }}>
            Tes objets et tes photos sont bien à l’abri. Recharge l’app pour reprendre là où tu en étais.
          </p>
          <div style={{ marginTop: 28 }}>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                width: '100%',
                border: 'none',
                cursor: 'pointer',
                borderRadius: 12,
                background: PALETTE.brand,
                color: '#ffffff',
                padding: '12px 24px',
                fontSize: 16,
                fontWeight: 600,
                fontFamily: 'inherit',
                boxShadow: '0 2px 12px rgba(63, 146, 138, 0.25)',
              }}
            >
              Recharger l’app
            </button>
            <button
              type="button"
              onClick={() => {
                window.location.href = `mailto:help@liberio.app?subject=${encodeURIComponent('Souci dans l’app Liberio')}`;
              }}
              style={{
                width: '100%',
                border: 'none',
                cursor: 'pointer',
                background: 'transparent',
                marginTop: 12,
                padding: '10px 24px',
                fontSize: 14,
                fontWeight: 500,
                fontFamily: 'inherit',
                color: PALETTE.body,
              }}
            >
              Le souci persiste ? Écris-nous
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
